import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { UserCheck, X } from "lucide-react";

interface UserRow {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    role_name: string;
    is_active: boolean;
}

/** Thin banner shown while an admin is acting as another user. */
export function ImpersonationBanner({ headerOffset = "4.6875rem" }: { headerOffset?: string }) {
    const { actingAs, user, stopImpersonating } = useAuth();
    const navigate = useNavigate();
    if (!actingAs || !user) return null;
    return (
        <div
            style={{ top: headerOffset }}
            className="sticky z-30 flex items-center justify-center gap-3 bg-amber-500 text-amber-950 px-4 py-1.5 text-sm font-medium"
        >
            <UserCheck className="h-4 w-4" />
            <span>
                Viewing as <strong>{user.display_name}</strong> ({user.role}) — impersonation mode
            </span>
            <Button
                size="sm"
                variant="outline"
                className="h-6 bg-amber-100 border-amber-700 text-amber-950 hover:bg-amber-50"
                onClick={() => {
                    void stopImpersonating().then(() => navigate("/"));
                }}
            >
                <X className="h-3 w-3 mr-1" /> Return to admin
            </Button>
        </div>
    );
}

/** Admin dialog to switch into another user's session for testing. */
export function UserSwitcherDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
    const { impersonate } = useAuth();
    const navigate = useNavigate();
    const [q, setQ] = useState("");
    const [busyId, setBusyId] = useState<string | null>(null);

    const { data } = useQuery({
        queryKey: ["impersonation-users"],
        queryFn: () => api.get<{ users: UserRow[] }>("/api/users?limit=200"),
        enabled: open,
    });

    const users = useMemo(() => {
        const all = (data?.users ?? []).filter((u) => u.is_active);
        const term = q.trim().toLowerCase();
        if (!term) return all;
        return all.filter((u) =>
            `${u.first_name} ${u.last_name} ${u.email} ${u.role_name}`.toLowerCase().includes(term),
        );
    }, [data, q]);

    const pick = (id: string) => {
        setBusyId(id);
        void impersonate(id)
            .then(() => {
                onClose();
                navigate("/");
            })
            .finally(() => setBusyId(null));
    };

    return (
        <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
            <SheetContent className="w-full sm:max-w-md overflow-y-auto">
                <SheetHeader>
                    <SheetTitle>Switch user</SheetTitle>
                    <SheetDescription>
                        Assume another user&apos;s view for testing. All actions are audited as you acting as them.
                    </SheetDescription>
                </SheetHeader>
                <div className="mt-4 space-y-3">
                    <Input
                        placeholder="Search by name, email, or role…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                    />
                    <ul className="space-y-1">
                        {users.map((u) => (
                            <li key={u.id}>
                                <button
                                    disabled={busyId !== null}
                                    onClick={() => pick(u.id)}
                                    className="flex w-full items-center justify-between rounded-md border p-2 text-left text-sm hover:bg-muted disabled:opacity-50"
                                >
                                    <span>
                                        <span className="font-medium">
                                            {u.first_name} {u.last_name}
                                        </span>
                                        <span className="block text-xs text-muted-foreground">{u.email}</span>
                                    </span>
                                    <Badge variant="outline">{u.role_name}</Badge>
                                </button>
                            </li>
                        ))}
                        {users.length === 0 && (
                            <li className="p-3 text-sm text-muted-foreground">No matching users.</li>
                        )}
                    </ul>
                </div>
            </SheetContent>
        </Sheet>
    );
}
